(() => {
  if (window.__tw26ProductCardVariantBridgeV3Loaded) return;
  window.__tw26ProductCardVariantBridgeV3Loaded = true;

  const cardSelector = '.tw26-product-card';
  let cardObserver = null;

  const addWidth = (url, width = 1200) => {
    if (!url) return '';

    try {
      const parsed = new URL(url, document.baseURI);
      parsed.searchParams.set('width', String(width));
      return parsed.toString();
    } catch (error) {
      return url;
    }
  };

  const hydrateDeferredImages = (card, slider) => {
    const images = Array.from(card.querySelectorAll('[data-tw26-deferred-image][data-tw26-deferred-src]'));
    if (!images.length) return;

    const baseWidth = slider?.clientWidth || card.clientWidth || 360;
    const pixelRatio = Math.min(window.devicePixelRatio || 1, 2);
    const targetWidth = Math.min(900, Math.max(360, Math.ceil(baseWidth * pixelRatio)));

    images.forEach((image) => {
      const source = image.dataset.tw26DeferredSrc;
      if (!source) return;

      image.loading = 'eager';
      image.src = addWidth(source, targetWidth);
      image.removeAttribute('data-tw26-deferred-src');
      image.removeAttribute('data-tw26-deferred-image');
    });
  };

  const initCard = (card) => {
    if (!card || card.dataset.tw26VariantBridgeReady === 'true') return;

    const slider = card.querySelector('[data-tw26-slider]');
    const slides = Array.from(card.querySelectorAll('[data-tw26-slide]'));
    const dots = Array.from(card.querySelectorAll('[data-tw26-dot]'));
    const firstImage = slides[0]?.querySelector('img');
    const variantInput = card.querySelector('input[name="id"]');
    const picker = card.querySelector('[data-pcard-variant-picker]');
    const variantsScript = picker?.querySelector('script[type="application/json"]');

    if (!slider || !slides.length || !firstImage) return;

    card.dataset.tw26VariantBridgeReady = 'true';

    let variants = [];
    let frame = 0;
    let syncTimer = 0;
    let startX = 0;
    let startY = 0;
    let dragged = false;

    if (variantsScript?.textContent) {
      try {
        variants = JSON.parse(variantsScript.textContent);
      } catch (error) {
        console.warn('[Teenwear] Product card variants JSON could not be parsed.', error);
      }
    }

    const updateDots = () => {
      frame = 0;
      if (!dots.length) return;

      const slideWidth = slider.clientWidth || 1;
      const activeIndex = Math.max(
        0,
        Math.min(dots.length - 1, Math.round(Math.abs(slider.scrollLeft) / slideWidth))
      );

      dots.forEach((dot, index) => {
        const active = index === activeIndex;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };

    const showVariantImage = (source) => {
      if (!source) return;

      firstImage.removeAttribute('srcset');
      firstImage.removeAttribute('sizes');
      firstImage.src = addWidth(source);
      firstImage.dataset.tw26VariantSource = source;

      slides.forEach((slide, index) => {
        slide.classList.toggle('is-active', index === 0);
      });

      if (typeof slider.scrollTo === 'function') {
        slider.scrollTo({ left: 0, behavior: 'smooth' });
      } else {
        slider.scrollLeft = 0;
      }

      window.requestAnimationFrame(updateDots);
    };

    const getSelectedVariant = () => {
      if (!variantInput || !variants.length) return null;
      const variantId = Number(variantInput.value || variantInput.getAttribute('value'));
      if (!variantId) return null;
      return variants.find((variant) => Number(variant.id) === variantId) || null;
    };

    const syncSelectedVariant = () => {
      syncTimer = 0;
      const variant = getSelectedVariant();
      const source = variant?.featured_image?.src;
      if (source) showVariantImage(source);
    };

    const queueVariantSync = () => {
      if (syncTimer) window.clearTimeout(syncTimer);
      syncTimer = window.setTimeout(syncSelectedVariant, 0);
    };

    slider.addEventListener(
      'scroll',
      () => {
        if (!frame) frame = window.requestAnimationFrame(updateDots);
      },
      { passive: true }
    );

    slider.addEventListener(
      'touchstart',
      (event) => {
        hydrateDeferredImages(card, slider);

        const touch = event.touches[0];
        if (!touch) return;
        startX = touch.clientX;
        startY = touch.clientY;
        dragged = false;
      },
      { passive: true }
    );

    slider.addEventListener(
      'touchmove',
      (event) => {
        const touch = event.touches[0];
        if (!touch) return;

        const deltaX = Math.abs(touch.clientX - startX);
        const deltaY = Math.abs(touch.clientY - startY);
        if (deltaX > 8 && deltaX > deltaY) dragged = true;
      },
      { passive: true }
    );

    slider.addEventListener(
      'click',
      (event) => {
        if (!dragged) return;
        event.preventDefault();
        event.stopPropagation();
        dragged = false;
      },
      true
    );

    slider.addEventListener('dragstart', (event) => event.preventDefault());

    card.addEventListener('product-card:option-selected', (event) => {
      const source = event.detail?.image || event.detail?.variant?.featured_image?.src;
      if (source) showVariantImage(source);
    });

    card.addEventListener('click', (event) => {
      const option = event.target.closest('.m-product-option--node__label');
      if (!option || !card.contains(option)) return;
      if (option.classList.contains('m-product-quickview-button')) return;
      queueVariantSync();
    });

    card.addEventListener('change', (event) => {
      if (!event.target.closest('[data-pcard-variant-picker]')) return;
      queueVariantSync();
    });

    if (variantInput) {
      new MutationObserver(queueVariantSync).observe(variantInput, {
        attributes: true,
        attributeFilter: ['value'],
      });
    }

    if ('ResizeObserver' in window) {
      const resizeObserver = new ResizeObserver(updateDots);
      resizeObserver.observe(slider);
    }

    updateDots();
  };

  const observeCard = (card) => {
    if (!card || card.dataset.tw26VariantBridgeReady === 'true') return;
    if (card.dataset.tw26Observed === 'true') return;

    if (!('IntersectionObserver' in window)) {
      initCard(card);
      return;
    }

    if (!cardObserver) {
      cardObserver = new IntersectionObserver(
        (entries, observer) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);
            delete entry.target.dataset.tw26Observed;
            initCard(entry.target);
          });
        },
        { rootMargin: '800px 0px' }
      );
    }

    card.dataset.tw26Observed = 'true';
    cardObserver.observe(card);
  };

  const initWithin = (root) => {
    const scope = root || document;

    if (scope.matches?.(cardSelector)) {
      observeCard(scope);
    }

    scope.querySelectorAll?.(cardSelector).forEach(observeCard);
  };

  const boot = () => initWithin(document);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }

  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === 1) initWithin(node);
      });
    });
  }).observe(document.documentElement, {
    childList: true,
    subtree: true,
  });

  document.addEventListener('shopify:section:load', (event) => {
    initWithin(event.target || document);
  });
})();
